/// <amd-module name="Test/TestResultsSettings/Store/TestResultsSettingsStore" />
import * as BuildContracts from "Build/Client/Contracts/Build";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import * as TestManagementService from "Test/Client/TestManagementServices";
import { getProjectName } from "Test/common-util/Common.Utils";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import * as Resources from "Test/TestResultsSettings/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { Store } from "VSS/Reflux/Store";
import { IListBoxItem } from "VSSUI/ListBox";

export interface ITestResultsSettingsStoreOptions {
    pageContext: IVssPageContext;
}

export interface ITestResultsSettingsPropertyState<T> {
    settingsObservable: ObservableValue<T>;
    settingsData: T;
}

export interface ITestResultsSettingsState {
    loading: boolean;
    serverError: boolean;
    serverMessage: string;
    settingsToggleButton: ITestResultsSettingsPropertyState<boolean>;
    settingsTypeState: ITestResultsSettingsPropertyState<string>;
    selectedPipeline: ITestResultsSettingsPropertyState<string>;
    settingsAllowedPipelines: ITestResultsSettingsPropertyState<number[]>;
    summaryReportSettingsState: ITestResultsSettingsPropertyState<boolean>;
    manualMarkUnmarkState: ITestResultsSettingsPropertyState<boolean>;
    pipelinesMap: ITestResultsSettingsPropertyState<Map<string, IListBoxItem<{}>>>;
    storeNewTestsState: ITestResultsSettingsPropertyState<boolean>;
    isFlakyDetectionTypeChangeDialogOpen: ObservableValue<boolean>;
}

export class TestResultsSettingsStore extends Store {
    private _state: ITestResultsSettingsState;
    private _buildRestClient: IBuildRestClient;
    private _pipelinesResultCache: any = {};

    constructor(private options: ITestResultsSettingsStoreOptions) {
        super("");
        this._state = this._getInitialState();
        this._buildRestClient = this.options.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");
    }

    public getState(): ITestResultsSettingsState {
        return this._state;
    }

    public async getTestResultsSettings() {
        try {
            const tcmService = this.options.pageContext.getService<TestManagementService.ITestManagementService>(
                TestManagementService.TestManagementServiceName
            );
            const testResultsSettings = await tcmService.getTestResultsSettings(TCMContracts.TestResultsSettingsType.All);
            if (!!testResultsSettings) {
                this._updateTestResultsSettingsData(testResultsSettings);
            }
        } catch (error) {
            this._updateDataOnError(error);
        }
    }

    public async updateTestResultsSettings(testResultsUpdateSettings: TCMContracts.TestResultsUpdateSettings): Promise<boolean> {
        try {
            const tcmService = this.options.pageContext.getService<TestManagementService.ITestManagementService>(
                TestManagementService.TestManagementServiceName
            );
            const testResultsSettings = await tcmService.updatePipelinesTestSettings(testResultsUpdateSettings);
            if (!!testResultsSettings) {
                this._updateTestResultsSettingsData(testResultsSettings);
            }
        } catch (error) {
            this._updateDataOnError(error);
            return false;
        }
        return true;
    }

    public async getPipelines() {
        try {
            const projectName = getProjectName(this.options.pageContext);
            if (!!this._pipelinesResultCache[projectName]) {
                this._populatePipelineMap(this._pipelinesResultCache[projectName]);
            } else {
                const results: BuildContracts.BuildDefinitionReference[] = await this._buildRestClient.getDefinitions(projectName);
                if (!!results) {
                    this._pipelinesResultCache[projectName] = results;
                    this._populatePipelineMap(results);
                }
            }
        } catch (error) {
            this._updateDataOnError(error);
        }
    }

    private _updateTestResultsSettingsData = (testResultsSettingsData: TCMContracts.TestResultsSettings) => {
        if (!!testResultsSettingsData) {
            if (this._isFlakyDetectionToggledOff(testResultsSettingsData)) {
                this._resetTestFlakySettingsData();
            } else {
                this._populateTestFlakySettingsData(testResultsSettingsData);
            }
            if(!!testResultsSettingsData.newTestResultLoggingSettings){
                this._populateNewTestLoggingSettings(testResultsSettingsData.newTestResultLoggingSettings);
            }
        }
        this._state.loading = false;
        this._state.serverError = false;
        this.emitChanged();
    };

    private _updateDataOnError = (error: any) => {
        this._revertChanges();
        this._state.loading = false;
        this._state.serverMessage = !!error ? (!!error.message ? error.message : Resources.ErrorToastMessage) : Resources.ErrorToastMessage;
        this._state.serverError = true;
        this.emitChanged();
    };

    private _revertChanges() {
        this._state.settingsToggleButton.settingsObservable.value = this._state.settingsToggleButton.settingsData;
        this._state.summaryReportSettingsState.settingsObservable.value = this._state.summaryReportSettingsState.settingsData;
        this._state.manualMarkUnmarkState.settingsObservable.value = this._state.manualMarkUnmarkState.settingsData;
        this._state.settingsAllowedPipelines.settingsObservable.value = this._state.settingsAllowedPipelines.settingsData;
        this._state.selectedPipeline.settingsObservable.value = this._state.selectedPipeline.settingsData;
        this._state.settingsTypeState.settingsObservable.value = this._state.settingsTypeState.settingsData;
        this._state.storeNewTestsState.settingsObservable.value = this._state.storeNewTestsState.settingsData;
    }

    private _isFlakyDetectionToggledOff(testResultsSettingsData: TCMContracts.TestResultsSettings): boolean {
        return (
            testResultsSettingsData == null ||
            testResultsSettingsData.flakySettings == null ||
            (testResultsSettingsData.flakySettings.flakyDetection == null &&
                testResultsSettingsData.flakySettings.flakyInSummaryReport == null &&
                testResultsSettingsData.flakySettings.manualMarkUnmarkFlaky == null)
        );
    }

    private _resetTestFlakySettingsData() {
        this._state.settingsToggleButton.settingsData = false;
        this._state.settingsToggleButton.settingsObservable.value = false;

        this._state.settingsTypeState.settingsData = Resources.SystemDetectionText;
        this._state.settingsTypeState.settingsObservable.value = Resources.SystemDetectionText;

        this._state.selectedPipeline.settingsData = Resources.AllPipelinesText;
        this._state.selectedPipeline.settingsObservable.value = Resources.AllPipelinesText;

        this._state.summaryReportSettingsState.settingsData = true;
        this._state.summaryReportSettingsState.settingsObservable.value = true;

        this._state.manualMarkUnmarkState.settingsData = false;
        this._state.manualMarkUnmarkState.settingsObservable.value = false;
    }

    private _populateTestFlakySettingsData(testResultsSettingsData: TCMContracts.TestResultsSettings) {
        this._state.settingsToggleButton.settingsData = true;
        this._state.settingsToggleButton.settingsObservable.value = true;

        this._mapFlakyDetectionType(testResultsSettingsData.flakySettings.flakyDetection);

        this._state.summaryReportSettingsState.settingsData =
            testResultsSettingsData.flakySettings.flakyInSummaryReport == null ? true : testResultsSettingsData.flakySettings.flakyInSummaryReport;
        this._state.summaryReportSettingsState.settingsObservable.value = this._state.summaryReportSettingsState.settingsData;

        this._state.manualMarkUnmarkState.settingsData =
            testResultsSettingsData.flakySettings.manualMarkUnmarkFlaky == null ? false : testResultsSettingsData.flakySettings.manualMarkUnmarkFlaky;
        this._state.manualMarkUnmarkState.settingsObservable.value = this._state.manualMarkUnmarkState.settingsData;
    }

    private _populateNewTestLoggingSettings(newTestResultLoggingSettingsData: TCMContracts.NewTestResultLoggingSettings) {
        this._state.storeNewTestsState.settingsData = newTestResultLoggingSettingsData.logNewTests == null ? false : newTestResultLoggingSettingsData.logNewTests;
        this._state.storeNewTestsState.settingsObservable.value = this._state.storeNewTestsState.settingsData;
    }

    private _mapFlakyDetectionType(flakyDetection: TCMContracts.FlakyDetection) {
        if (flakyDetection !== null) {
            if (flakyDetection.flakyDetectionType === TCMContracts.FlakyDetectionType.System) {
                this._state.settingsTypeState.settingsData = Resources.SystemDetectionText;
                if (flakyDetection.flakyDetectionPipelines.isAllPipelinesAllowed) {
                    this._state.selectedPipeline.settingsData = Resources.AllPipelinesText;
                    this._state.selectedPipeline.settingsObservable.value = this._state.selectedPipeline.settingsData;

                    this._state.settingsAllowedPipelines.settingsData = [];
                    this._state.settingsAllowedPipelines.settingsObservable.value = [];
                } else {
                    this._state.settingsAllowedPipelines.settingsData = flakyDetection.flakyDetectionPipelines.allowedPipelines;
                    this._state.settingsAllowedPipelines.settingsObservable.value = this._state.settingsAllowedPipelines.settingsData;
                    this._state.selectedPipeline.settingsData = Resources.SpecificPipelinesText;
                    this._state.selectedPipeline.settingsObservable.value = this._state.selectedPipeline.settingsData;
                }
            } else {
                this._state.settingsTypeState.settingsData = Resources.CustomDetectionText;
            }
        } else {
            this._state.settingsTypeState.settingsData = Resources.SystemDetectionText;
        }
        this._state.settingsTypeState.settingsObservable.value = this._state.settingsTypeState.settingsData;
    }

    private _getInitialState() {
        const map = new Map<string, IListBoxItem<{}>>();
        return {
            loading: true,
            serverError: false,
            isFlakyDetectionTypeChangeDialogOpen: new ObservableValue<boolean>(false),
            settingsTypeState: {
                settingsObservable: new ObservableValue<string>(Resources.SystemDetectionText),
                settingsData: Resources.SystemDetectionText
            } as ITestResultsSettingsPropertyState<string>,
            settingsToggleButton: {
                settingsObservable: new ObservableValue<boolean>(false),
                settingsData: false
            } as ITestResultsSettingsPropertyState<boolean>,
            selectedPipeline: {
                settingsObservable: new ObservableValue<string>(Resources.AllPipelinesText),
                settingsData: Resources.AllPipelinesText
            } as ITestResultsSettingsPropertyState<string>,
            settingsAllowedPipelines: {
                settingsObservable: new ObservableValue<number[]>([]),
                settingsData: []
            } as ITestResultsSettingsPropertyState<number[]>,
            summaryReportSettingsState: {
                settingsObservable: new ObservableValue<boolean>(true),
                settingsData: true
            } as ITestResultsSettingsPropertyState<boolean>,
            manualMarkUnmarkState: {
                settingsObservable: new ObservableValue<boolean>(false),
                settingsData: false
            } as ITestResultsSettingsPropertyState<boolean>,
            pipelinesMap: {
                settingsObservable: new ObservableValue<Map<string, IListBoxItem<{}>>>(map),
                settingsData: map
            } as ITestResultsSettingsPropertyState<Map<string, IListBoxItem<{}>>>,
            storeNewTestsState: {
                settingsObservable: new ObservableValue<boolean>(false),
                settingsData: false
            } as ITestResultsSettingsPropertyState<boolean>
        } as ITestResultsSettingsState;
    }

    private _populatePipelineMap(buildDefinitionReferences: BuildContracts.BuildDefinitionReference[]) {
        const map = new Map<string, IListBoxItem<{}>>();
        buildDefinitionReferences.forEach(buildDefinitionReference => {
            const listTem = {
                id: buildDefinitionReference.id.toString(),
                text: buildDefinitionReference.name
            } as IListBoxItem;
            map.set(listTem.id.toString(), listTem);
        });
        this._state.pipelinesMap.settingsData = map;
        this._state.pipelinesMap.settingsObservable.value = this._state.pipelinesMap.settingsData;
        this.emitChanged();
    }
}
